import React, { useState } from "react";
import { api, apiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Building2, Users, CheckCircle2, Award } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import SiteNav from "@/components/SiteNav";
import { INSTITUTION } from "@/constants/testIds";

export default function Institutions() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const lookup = async () => {
    if (!code.trim()) { setError("Enter your institution code or name."); return; }
    setLoading(true); setError(""); setData(null);
    try {
      const { data } = await api.get(`/institution/${encodeURIComponent(code.trim())}`);
      setData(data);
    } catch (e) { setError(apiError(e, "No records found for this institution.")); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteNav />
      <section className="max-w-5xl mx-auto px-5 py-14">
        <div className="flex items-center gap-2 text-blue-700"><Building2 className="w-5 h-5" /><span className="eyebrow">For Institutions</span></div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-2 max-w-3xl">Batch analytics dashboard.</h1>
        <p className="text-slate-500 mt-4 max-w-2xl text-lg">Enter your institution code (or name) to see completion progress and domain-wise averages across your candidates' MSOS results.</p>

        <div className="flex gap-3 mt-8 max-w-md">
          <Input data-testid={INSTITUTION.codeInput} value={code} onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. IIM Bangalore" onKeyDown={(e) => e.key === "Enter" && lookup()} className="h-11" />
          <Button data-testid={INSTITUTION.lookupButton} onClick={lookup} disabled={loading}
            className="h-11 rounded-full bg-blue-700 hover:bg-blue-800 text-white shrink-0">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "View dashboard"}
          </Button>
        </div>
        {error && <p data-testid={INSTITUTION.error} className="text-sm text-red-600 mt-3">{error}</p>}

        {data && (
          <div data-testid={INSTITUTION.summary} className="mt-10 stagger">
            <h2 className="text-2xl font-bold">{data.institution}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
              <Kpi icon={Users} label="Total Test IDs" value={data.total} />
              <Kpi icon={CheckCircle2} label="Completed" value={data.completed} />
              <Kpi icon={Award} label="MSOS Completed" value={data.msosCompleted} />
              <Kpi icon={Building2} label="Code" value={data.code} small />
            </div>

            {data.domainAverages.length > 0 ? (
              <>
                <div className="bg-white border border-slate-200 rounded-lg p-6 mt-6">
                  <p className="eyebrow mb-4">Domain-wise average (%) — MSOS</p>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={data.domainAverages} margin={{ left: 10 }}>
                      <XAxis dataKey="domain" tick={{ fill: "#475569", fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
                      <YAxis domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="avg" radius={[4, 4, 0, 0]}>
                        {data.domainAverages.map((_, i) => <Cell key={i} fill={i === 0 ? "#0F172A" : "#1D4ED8"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {data.recommendedDistribution.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg p-6 mt-6">
                    <p className="eyebrow mb-4">Recommended specialization distribution</p>
                    <div className="space-y-2">
                      {data.recommendedDistribution.map((r) => (
                        <div key={r.domain} className="flex items-center justify-between text-sm">
                          <span className="text-slate-700">{r.domain}</span>
                          <span className="font-semibold text-blue-700">{r.count} candidate{r.count > 1 ? "s" : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-slate-500 mt-6">No completed MSOS assessments yet for this institution.</p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, small }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-center gap-2 text-slate-400 mb-2"><Icon className="w-4 h-4" /><span className="eyebrow">{label}</span></div>
      <div className={small ? "text-lg font-bold font-mono" : "text-3xl font-bold"}>{value}</div>
    </div>
  );
}
